import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const FuelRetailPage = () => {
  const capabilities = [
    {
      title: 'Site Feasibility & Market Analysis',
      description: 'Location analysis, market research, and feasibility studies for new gas station developments.',
      icon: 'ri-map-pin-line',
    },
    {
      title: 'Margin & Fuel Pricing Strategy',
      description: 'Fuel pricing optimization, margin analysis, and competitive positioning strategies.',
      icon: 'ri-price-tag-3-line',
    },
    {
      title: 'Store Layout & Merchandising',
      description: 'Convenience store design, product mix optimization, and merchandising strategies.',
      icon: 'ri-store-3-line',
    },
    {
      title: 'Vendor & Fuel Supply Contracts',
      description: 'Supplier negotiation, contract review, and fuel supply agreement optimization.',
      icon: 'ri-file-text-line',
    },
    {
      title: 'Financing & Redevelopment Advisory',
      description: 'Capital planning, financing options, and site redevelopment strategic guidance.',
      icon: 'ri-funds-line',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                  <span className="text-xs uppercase tracking-wider text-copper">Fuel Retail Consulting</span>
                </div>
                <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                  Gasoline Retail &{' '}
                  <span className="bg-copper-gradient bg-clip-text text-transparent">
                    Convenience Store Consulting
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed mb-10">
                  Specialized consulting for gas station owners, convenience store operators, and fuel retail networks seeking growth, profitability, and operational excellence.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/bea1d67524fee28764fed782fb9d5f66.jpg"
                  alt="Fuel Retail Consulting"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Fuel Retail Capabilities</h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Comprehensive consulting services designed to maximize profitability, optimize operations, and drive growth in the competitive fuel retail market.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-charcoal border border-charcoal-light rounded-2xl p-8 hover:border-copper transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-4xl text-copper`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{capability.title}</h3>
                  <p className="text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Industry Expertise</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  Our fuel retail consulting practice combines deep industry knowledge with financial acumen to help gas station owners and convenience store operators maximize profitability. We understand the unique challenges of fuel retail and deliver practical solutions that drive results.
                </p>
                <ul className="space-y-4">
                  {[
                    'New site development and feasibility analysis',
                    'Competitive market analysis and positioning',
                    'Fuel margin optimization and pricing strategies',
                    'Convenience store merchandising and layout design',
                    'Supplier contract negotiation and optimization',
                    'Financing strategies and capital planning',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-copper text-xl mt-1"></i>
                      <span className="text-base text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=convenience%20store%20interior%20with%20organized%20product%20displays%20and%20modern%20merchandising%20layout%2C%20professional%20retail%20environment%20with%20clean%20design%20and%20efficient%20customer%20flow%2C%20commercial%20photography%20showing%20well-stocked%20shelves&width=700&height=400&seq=fuel-expertise-002&orientation=landscape"
                  alt="Industry Expertise"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Maximize Your Fuel Retail Profitability
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Partner with MACGOC to optimize operations and drive sustainable growth in fuel retail.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FuelRetailPage;